import { apiCustomer } from "@/api"

/**
 * Geocoding goes through the customer gateway proxy (/geocode) instead of
 * calling maps.googleapis.com directly. The gateway tries free providers first
 * (BrasilAPI CEP V2, ViaCEP, OpenCEP, Photon, Nominatim, CPTEC+Open-Meteo) and
 * only falls back to Google as a last resort. Results are cached in Redis,
 * collapsing repeated CEP/address/pin lookups across all customers into cache
 * hits. The response shape remains Google Geocoding JSON for compatibility.
 *
 * Uses the AUTHENTICATED client (apiCustomer) on purpose: /geocode is behind
 * JWTAuth. Prefer BrasilAPI CEP V2 on the client for postal-code fills so most
 * lookups never reach this proxy (and never Google).
 */

export type GoogleAddressComponent = {
  long_name: string
  short_name: string
  types: string[]
}

export type GoogleGeometry = {
  location: {
    lat: number
    lng: number
  }
  location_type: string
  viewport: {
    northeast: { lat: number; lng: number }
    southwest: { lat: number; lng: number }
  }
}

export type GoogleGeocodingResult = {
  address_components: GoogleAddressComponent[]
  formatted_address: string
  geometry: GoogleGeometry
  place_id: string
  types: string[]
}

export type GoogleGeocodingResponse = {
  results: GoogleGeocodingResult[]
  status: string
}

const sessionCache = new Map<string, GoogleGeocodingResponse>()

const normalizeAddress = (address: string): string =>
  address.trim().toLowerCase().replace(/\s+/g, " ")

const roundCoord = (value: number): string => value.toFixed(5)

async function requestGeocode(
  params: Record<string, string>,
  cacheKey: string,
): Promise<GoogleGeocodingResponse> {
  const cached = sessionCache.get(cacheKey)
  if (cached) return cached

  const { data } = await apiCustomer.get<GoogleGeocodingResponse>("/geocode", { params })

  if (data.status === "OK" || data.status === "ZERO_RESULTS") {
    sessionCache.set(cacheKey, data)
  }

  return data
}

/** Forward geocode: full address string -> coordinates + components. */
export async function forwardGeocode(
  address: string,
  region = "br",
): Promise<GoogleGeocodingResponse> {
  return requestGeocode(
    { address, region },
    `fwd:${region}:${normalizeAddress(address)}`,
  )
}

/** Reverse geocode: a pin's coordinates -> address components. */
export async function reverseGeocode(lat: number, lng: number): Promise<GoogleGeocodingResponse> {
  return requestGeocode(
    { latlng: `${lat},${lng}` },
    `rev:${roundCoord(lat)},${roundCoord(lng)}`,
  )
}
